import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  PHONE_COUNTRY_OPTIONS,
  getPhoneNumberWithCountryCode,
  getPhoneCountryByCode,
  sanitizeNationalNumber,
} from "@/shared/utils/phoneCountryCodes";

const cn = (...inputs) => twMerge(clsx(inputs));

const CommonPhoneInput = forwardRef(
  (
    {
      countryCode = DEFAULT_PHONE_COUNTRY_CODE,
      phoneNumber = "",
      onChange,
      placeholder = "Enter phone number",
      disabled = false,
      className = "",
      inputClassName = "",
      defaultCountryCode = DEFAULT_PHONE_COUNTRY_CODE,
    },
    ref,
  ) => {
    const containerRef = useRef(null);
    const dropdownRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [dropdownPosition, setDropdownPosition] = useState(null);

    const selectedCountry = useMemo(
      () => getPhoneCountryByCode(countryCode || defaultCountryCode),
      [countryCode, defaultCountryCode],
    );

    const filteredCountries = useMemo(() => {
      const q = searchTerm.trim().toLowerCase();
      if (!q) return PHONE_COUNTRY_OPTIONS;
      return PHONE_COUNTRY_OPTIONS.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.code.toLowerCase().includes(q) ||
          c.dialCode.includes(q),
      );
    }, [searchTerm]);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(e.target) &&
          !dropdownRef.current?.contains(e.target)
        ) {
          setIsOpen(false);
          setSearchTerm("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
      if (!isOpen) return;

      const updateDropdownPosition = () => {
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const gap = 4;
        const edge = 8;
        const searchHeight = 38;
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - rect.bottom - gap - edge;
        const spaceAbove = rect.top - gap - edge;
        const openAbove = spaceBelow < 96 && spaceAbove > spaceBelow;
        const availableHeight = Math.max(
          72,
          openAbove ? spaceAbove : spaceBelow,
        );
        const listHeight = Math.max(
          48,
          Math.min(200, availableHeight - searchHeight),
        );
        const width = Math.min(240, rect.width, viewportWidth - edge * 2);
        const left = Math.min(
          Math.max(edge, rect.left),
          viewportWidth - width - edge,
        );

        setDropdownPosition({
          left,
          top: openAbove
            ? Math.max(edge, rect.top - gap - searchHeight - listHeight)
            : rect.bottom + gap,
          width,
          listHeight,
        });
      };

      updateDropdownPosition();
      window.addEventListener("resize", updateDropdownPosition);
      window.addEventListener("scroll", updateDropdownPosition, true);
      return () => {
        window.removeEventListener("resize", updateDropdownPosition);
        window.removeEventListener("scroll", updateDropdownPosition, true);
      };
    }, [isOpen]);

    const emitChange = (nextCountryCode, nextNational) => {
      const national = sanitizeNationalNumber(nextNational);
      onChange?.({
        countryCode: nextCountryCode,
        nationalNumber: national,
        e164: getPhoneNumberWithCountryCode(nextCountryCode, national),
      });
    };

    const handleCountrySelect = (code) => {
      emitChange(code, phoneNumber);
      setIsOpen(false);
      setSearchTerm("");
    };

    const handlePhoneChange = (e) => {
      emitChange(countryCode || defaultCountryCode, e.target.value);
    };

    return (
      <div
        ref={containerRef}
        className={cn(
          "relative flex w-full min-w-0 items-stretch rounded-lg transition-colors h-9",
          disabled
            ? "border border-gray-200 dark:border-gray-800 bg-gray-100 dark:bg-gray-800 cursor-not-allowed opacity-70"
            : "border border-gray-300 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm focus-within:border-secondary focus-within:border-[1.5px]",
          className,
        )}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setIsOpen((prev) => !prev)}
          className={cn(
            "flex shrink-0 items-center gap-1 border-r border-gray-300 dark:border-gray-800 px-2 outline-none",
            disabled
              ? "cursor-not-allowed"
              : "cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 hover:rounded-l-lg",
          )}
          aria-label="Country code"
          aria-expanded={isOpen}
        >
          <img
            src={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png`}
            alt=""
            className="h-4 w-4 rounded-full border border-gray-200 dark:border-gray-700 object-cover"
          />
          <span className="text-[0.715rem] font-medium text-gray-800 dark:text-gray-100">
            {selectedCountry.dialCode}
          </span>
          <ChevronDown size={14} className="text-gray-500 dark:text-gray-400 shrink-0" />
        </button>

        <input
          ref={ref}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          disabled={disabled}
          value={phoneNumber}
          onChange={handlePhoneChange}
          placeholder={placeholder}
          className={cn(
            "min-w-0 flex-1 bg-transparent px-3 py-2 text-[0.715rem] font-normal text-gray-800 dark:text-gray-100 outline-none ring-0",
            "placeholder:text-[0.715rem] placeholder:text-gray-400 dark:placeholder:text-gray-500",
            "my-0.5 mr-0.5 rounded-r-md",
            disabled && "cursor-not-allowed text-gray-400 dark:text-gray-500",
            inputClassName,
          )}
        />

        {isOpen &&
        !disabled &&
        dropdownPosition &&
        typeof document !== "undefined"
          ? createPortal(
              <div
                ref={dropdownRef}
                className="fixed z-[100000003] overflow-hidden rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-lg"
                style={{
                  left: dropdownPosition.left,
                  top: dropdownPosition.top,
                  width: dropdownPosition.width,
                }}
              >
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search country..."
                  className="w-full border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 px-3 py-2 text-xs text-gray-800 dark:text-gray-100 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500"
                  autoFocus
                />
                <div
                  className="overflow-y-auto"
                  style={{ maxHeight: dropdownPosition.listHeight }}
                >
                  {filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => handleCountrySelect(country.code)}
                      className={cn(
                        "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-gray-800 dark:text-gray-100 hover:bg-secondary-transparent dark:hover:bg-secondary/20 hover:text-secondary dark:hover:text-secondary-light",
                        country.code === selectedCountry.code &&
                          "bg-secondary-transparent dark:bg-secondary/20 text-secondary dark:text-secondary-light",
                      )}
                    >
                      <img
                        src={`https://flagcdn.com/w40/${country.code.toLowerCase()}.png`}
                        alt=""
                        className="h-4 w-4 rounded-full border border-gray-200 dark:border-gray-700 object-cover"
                      />
                      <span className="flex-1 truncate">{country.name}</span>
                      <span className="shrink-0 text-gray-500 dark:text-gray-400">
                        {country.dialCode}
                      </span>
                    </button>
                  ))}
                  {filteredCountries.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-500">
                      No countries found.
                    </p>
                  ) : null}
                </div>
              </div>,
              document.body,
            )
          : null}
      </div>
    );
  },
);

export default CommonPhoneInput;
